import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import { Button, InfiniteList } from 'components';
import { getStoreDetailMaterialData } from 'helpers/queriesHelper';
import { useCustomTranslation } from 'helpers/translationHelper';
import type {
  MaterialAtStore,
  SelectedMaterialItem,
  StoreMaterialTableQuery
} from 'pages/material/models';
import { useGetStoreDetailInfiniteQuery } from 'pages/material/queries';
import { useGetCodebooksQuery } from 'pages/settings/codebooks/queries';
import React, { useEffect, useState } from 'react';

import { defaultStoreMaterialTableQuery } from '../../../StoreDetailContent';
import MaterialListItem from '../MaterialListItem';

interface Props {
  selectedStoreId: string;
  searchPhrase?: string;
  selectedMaterial: SelectedMaterialItem[];
  setMaterialAmount: (item: MaterialAtStore, amount?: number | null) => void;
}

const StoreMaterialList: React.FC<Props> = (props): React.ReactElement => {
  const { searchPhrase, selectedStoreId, selectedMaterial, setMaterialAmount } = props;

  const { t } = useCustomTranslation();

  const [queryParams, setQueryParams] = useState<StoreMaterialTableQuery>({
    ...defaultStoreMaterialTableQuery,
    searchPhrase: searchPhrase
  });

  useEffect(() => {
    setQueryParams({ ...queryParams, searchPhrase });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPhrase]);

  const storeDetailQuery = useGetStoreDetailInfiniteQuery(selectedStoreId, queryParams);
  const codebooksQuery = useGetCodebooksQuery();
  const companyAndInfoQuery = useGetCompanyAndUserInfoQuery();

  const currencyId = companyAndInfoQuery.data?.company.currencyId || 'CZK';
  const materialCategories = codebooksQuery.data?.materialCategories;
  const materialSuppliers = codebooksQuery.data?.materialSuppliers;

  const materialData = getStoreDetailMaterialData(storeDetailQuery.data);

  const getStoredAmount = (material: MaterialAtStore, selected?: SelectedMaterialItem) => {
    if (
      (selected && material.amount === selected.amount) ||
      (selected && material.amount && selected.amount === undefined)
    ) {
      return 0;
    }

    if (material.amount) {
      return material.amount - (selected?.amount || 0);
    }

    return material.amount;
  };

  return (
    <div className="MaterialList">
      <div className="MaterialList_head">{t('stores.storedMaterial')}</div>
      <InfiniteList
        data={materialData}
        // data={[]}
        fetchNextPage={storeDetailQuery.fetchNextPage}
        isFetchingNextPage={storeDetailQuery.isFetchingNextPage}
        isLoading={storeDetailQuery.isLoading}
        renderItem={(item) => {
          const category = materialCategories?.find((x) => x.id === item.materialCategoryId);
          const supplier = materialSuppliers?.find((x) => x.id === item.materialSupplierId);

          const selected = selectedMaterial.find((x) => x.materialId === item.materialId);

          const isAllSelected = (selected && selected?.amount === undefined) || false;

          return (
            <MaterialListItem
              key={item.id}
              amount={selected?.amount || 0}
              categoryName={category?.name || ''}
              supplierName={supplier?.name || ''}
              description={item.description}
              identificationNumber={item.identificationNumber}
              maxAmount={item.amount || Infinity}
              name={item.name}
              onAmountChange={(amount) => setMaterialAmount(item, amount)}
              price={item.price || 0}
              supplierCode={item.supplierCode}
              storedAmount={getStoredAmount(item, selected)}
              unit={item.materialUnit}
              extraButton={
                <Button
                  onClick={() =>
                    isAllSelected
                      ? setMaterialAmount(item, 0)
                      : setMaterialAmount(item, item.amount)
                  }
                  danger={isAllSelected}
                >
                  {isAllSelected ? t('stores.unloaded') : t('stores.allAmountLabel')}
                </Button>
              }
              currencyId={currencyId}
              isAllSelected={isAllSelected}
            />
          );
        }}
      />
    </div>
  );
};

export default StoreMaterialList;
